<template>
    <RedesignBaseModal
        :visible="visible"
        :title="$t('widget.aiInstructions.modal.title')"
        width="50%"
        custom-class="ai-wizard-modal"
        @update:visible="onClose"
        @close="cancel"
    >
        <AiWizardStepper
            v-if="!editMode"
            :active-step="currentStep"
            class="shrink-0 mb-2"
        />

        <div class="ai-wizard-step-content overflow-y-auto">
            <AiWizardStartStep
                v-if="!editMode && currentStep === WIZARD_STEP.START"
                v-model="startMode"
            />

            <AiWizardTemplateStep
                v-else-if="currentStep === WIZARD_STEP.TEMPLATE"
                v-model="selectedTemplateId"
                :templates="promptTemplates"
                :is-loading="isLoadingTemplates"
            />

            <AiWizardCustomizeStep
                v-else-if="currentStep === WIZARD_STEP.CUSTOMIZE"
                :system-prompt="draftSystemPrompt"
                :user-prompt="draftUserPrompt"
                :show-template-banner="editMode"
                @change-system-prompt="draftSystemPrompt = $event"
                @change-user-prompt="draftUserPrompt = $event"
                @browse-templates="onSelectFromTemplate"
            />
        </div>

        <template #footer>
            <div class="w-full flex items-center justify-between gap-3">
                <div
                    v-if="footerError"
                    class="text-destructive-actions text-sm font-medium shake-animation"
                >
                    {{ footerError }}
                </div>

                <div class="ml-auto flex items-center gap-3">
                    <RedesignBaseButton
                        type="outline"
                        color="secondary"
                        @click="cancel"
                    >
                        {{ $t('common.cancel') }}
                    </RedesignBaseButton>

                    <RedesignBaseButton
                        v-if="showBackButton"
                        type="outline"
                        color="secondary"
                        @click="goBack"
                    >
                        {{ $t('general.back') }}
                    </RedesignBaseButton>

                    <RedesignBaseButton
                        v-if="!editMode && currentStep < WIZARD_STEP.CUSTOMIZE"
                        @click="goNext"
                    >
                        {{ $t('general.next') }}
                    </RedesignBaseButton>

                    <RedesignBaseButton
                        v-else-if="editMode && currentStep === WIZARD_STEP.TEMPLATE"
                        @click="applySelectedTemplate"
                    >
                        {{ $t('widget.aiInstructions.applyTemplate') }}
                    </RedesignBaseButton>

                    <RedesignBaseButton
                        v-else-if="currentStep === WIZARD_STEP.CUSTOMIZE"
                        @click="save"
                    >
                        {{ $t('common.save') }}
                    </RedesignBaseButton>
                </div>
            </div>
        </template>
    </RedesignBaseModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import i18n from '@/i18n'

import RedesignBaseModal from '@/components/RedesignComponents/Modal/RedesignBaseModal.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import AiWizardStepper from '@/components/RedesignComponents/Modal/AiWizard/AiWizardStepper.vue'
import AiWizardStartStep from '@/components/RedesignComponents/Modal/AiWizard/AiWizardStartStep.vue'
import AiWizardTemplateStep from '@/components/RedesignComponents/Modal/AiWizard/AiWizardTemplateStep.vue'
import AiWizardCustomizeStep from '@/components/RedesignComponents/Modal/AiWizard/AiWizardCustomizeStep.vue'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import useConfirmModal from '@/composables/useConfirmModal'
import { WidgetConfig } from '@/types/dashboard'
import { PROMPT_MIN_LENGTH, START_MODE_TEMPLATE, START_MODE_SCRATCH, WIZARD_STEP, StartMode } from '@/enum/aiInstructionTemplates'
import { widgetParameterApi, PromptTemplate } from '@/api/widgetParameterApi'

const props = withDefaults(defineProps<{
    visible: boolean
    config: WidgetConfig
    editMode?: boolean
}>(), {
    editMode: false
})

const emit = defineEmits<{
    (e: 'update:visible', v: boolean): void
    (e: 'save', value: WidgetConfig): void
}>()

const { closeWithConfirm } = useConfirmBeforeClose()

const currentStep = ref<number>(WIZARD_STEP.START)
const startMode = ref<StartMode>(START_MODE_TEMPLATE)
const selectedTemplateId = ref<number | undefined>(undefined)
const draftSystemPrompt = ref('')
const draftUserPrompt = ref('')
const footerError = ref('')

const promptTemplates = ref<PromptTemplate[]>([])
const isLoadingTemplates = ref(false)

async function fetchTemplates () {
    if (promptTemplates.value.length) return
    isLoadingTemplates.value = true
    try {
        promptTemplates.value = await widgetParameterApi.getPromptTemplatesList()
    } finally {
        isLoadingTemplates.value = false
    }
}

const showBackButton = computed(() => {
    if (props.editMode) return currentStep.value === WIZARD_STEP.TEMPLATE
    return currentStep.value > WIZARD_STEP.START
})


function goNext () {
    if (currentStep.value === WIZARD_STEP.START && startMode.value === START_MODE_SCRATCH) {
        draftSystemPrompt.value = ''
        draftUserPrompt.value = ''
        currentStep.value = WIZARD_STEP.CUSTOMIZE
        return
    }

    if (currentStep.value === WIZARD_STEP.TEMPLATE) {
        applySelectedTemplate()
        return
    }

    currentStep.value++
}

function goBack () {
    if (props.editMode && currentStep.value === WIZARD_STEP.TEMPLATE) {
        currentStep.value = WIZARD_STEP.CUSTOMIZE
        return
    }
    if (currentStep.value === WIZARD_STEP.CUSTOMIZE && startMode.value === START_MODE_SCRATCH) {
        currentStep.value = WIZARD_STEP.START
        return
    }
    currentStep.value--
}

function applySelectedTemplate () {
    const template = promptTemplates.value.find(t => t.PromptTemplateID === selectedTemplateId.value)
    if (template) {
        draftSystemPrompt.value = template.SystemPrompt
        draftUserPrompt.value = template.UserPrompt
    }
    currentStep.value = WIZARD_STEP.CUSTOMIZE
}

async function onSelectFromTemplate () {
    const isConfirmed = await useConfirmModal({
        header: String(i18n.t('general.defaultConfirmation')),
        message: String(i18n.t('widget.aiInstructions.overwriteConfirm')),
        type: 'confirm'
    })

    if (isConfirmed) {
        currentStep.value = WIZARD_STEP.TEMPLATE
    }
}

function validate (): boolean {
    const systemText = draftSystemPrompt.value.trim()
    const userText = draftUserPrompt.value.trim()
    const minLenMsg = i18n.t('common.minLength', { count: PROMPT_MIN_LENGTH })

    if (!systemText || systemText.length < PROMPT_MIN_LENGTH) {
        footerError.value = `${i18n.t('widget.aiInstructions.customize.systemPrompt')}: ${minLenMsg}`
        return false
    }

    if (!userText || userText.length < PROMPT_MIN_LENGTH) {
        footerError.value = `${i18n.t('widget.aiInstructions.customize.userPrompt')}: ${minLenMsg}`
        return false
    }

    footerError.value = ''
    return true
}

function save () {
    if (!validate()) return

    const newConfig: WidgetConfig = {
        ...props.config,
        WidgetParameterValueJson: {
            ...(props.config.WidgetParameterValueJson || {}),
            system: draftSystemPrompt.value,
            user: draftUserPrompt.value
        }
    }

    emit('save', newConfig)
    emit('update:visible', false)
}

function cancel () {
    closeWithConfirm(() => emit('update:visible', false))
}

function onClose (val: boolean) {
    if (!val) cancel()
}

function resetWizard () {
    startMode.value = START_MODE_TEMPLATE
    selectedTemplateId.value = undefined
    draftSystemPrompt.value = ''
    draftUserPrompt.value = ''
    footerError.value = ''
}

watch(
    () => props.visible,
    (open) => {
        if (!open) return

        resetWizard()
        fetchTemplates()

        const existing = props.config?.WidgetParameterValueJson

        if (props.editMode) {
            draftSystemPrompt.value = existing?.system || ''
            draftUserPrompt.value = existing?.user || ''
            currentStep.value = WIZARD_STEP.CUSTOMIZE
        } else if (existing?.system || existing?.user) {
            draftSystemPrompt.value = (existing.system) || ''
            draftUserPrompt.value = (existing.user) || ''
            currentStep.value = WIZARD_STEP.CUSTOMIZE
        } else {
            currentStep.value = WIZARD_STEP.START
        }
    },
    { immediate: true }
)
</script>

<style lang="scss" scoped>
.ai-wizard-step-content {
    @apply px-4;
    max-height: 350px;
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
    20%, 40%, 60%, 80% { transform: translateX(4px); }
}

.shake-animation {
    animation: shake 0.5s ease-in-out;
}
</style>
